<template>
  <HeaderBar />

  <ButtonBackContainer>
    <ButtonBack :fallback="{ name: 'couponList' }">{{ t('back') }}</ButtonBack>
    <AuditLogButton
      :entity-type="AuditLogEntityType.COUPON"
      :entity-id="route.params.id as string"
      class="ml-2"
    />
  </ButtonBackContainer>

  <div v-if="isLoading" class="flex justify-center py-8">
    <Spinner />
  </div>

  <template v-else-if="data">
    <section class="carvia-container">
      <div class="mb-6">
        <h1>{{ t('editCoupon') }}</h1>
      </div>

      <div class="mb-8 flex flex-wrap items-end justify-between gap-4 rounded-lg bg-white p-4">
        <div>
          <div class="mb-1 text-sm text-obsidian-600">{{ t('couponCode') }}</div>
          <div class="font-mono text-lg font-bold">{{ data.code }}</div>
        </div>
        <CVButton
          v-if="data.type === CouponType.PROMO"
          variant="secondary"
          @click="showPromoLinkModal = true"
        >
          {{ t('showCouponLink') }}
        </CVButton>
      </div>

      <PromoCouponLinkModal v-model="showPromoLinkModal" :code="data.code" />

      <CouponForm :initial-data="data" :submit-label="t('save')" @submit="onSubmit">
        <template #actions>
          <div class="flex w-full justify-between">
            <CVButton
              variant="danger"
              :loading="deleteMutation.isPending"
              @click.prevent="handleDelete"
            >
              {{ t('delete') }}
            </CVButton>
            <CVButton type="submit" :loading="updateMutation.isPending" variant="default">
              {{ t('save') }}
            </CVButton>
          </div>
        </template>
      </CouponForm>
    </section>

    <section class="carvia-container flex flex-col gap-4">
      <h2>{{ t('bookings') }}</h2>
      <CouponBookings
        :coupon-id="data.id"
        :show-generated-revenue="data.type === CouponType.PROMO"
      />
    </section>
  </template>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import type { Coupon } from '@/entities/coupons/coupon.entity';
import CouponForm from '../components/CouponForm.vue';
import PromoCouponLinkModal from '../components/PromoCouponLinkModal.vue';
import CouponBookings from '../components/CouponBookings.vue';
import HeaderBar from '@/components/headerbar/HeaderBar.vue';
import ButtonBack from '@/components/buttons/ButtonBack.vue';
import ButtonBackContainer from '@/components/ButtonBackContainer.vue';
import CVButton from '@/components/buttons/CVButton.vue';
import AuditLogButton from '@/components/audit-log/AuditLogButton.vue';
import { AuditLogEntityType } from '@carvia/ros-client-types';
import Spinner from '@/components/icons/Spinner.vue';
import { useI18n } from 'vue-i18n';
import { Alert } from '@/services/alert.service';
import { useCoupon, useDeleteCoupon, useUpdateCoupon } from '@/queries/use-coupons';
import { CouponType } from '@carvia/ros-client-types';

const { t } = useI18n();
const router = useRouter();
const route = useRoute();
const showPromoLinkModal = ref(false);

const { data, isLoading } = useCoupon(route.params.id as string);

const updateMutation = useUpdateCoupon();

const deleteMutation = useDeleteCoupon(() => {
  router.push({ name: 'couponList' });
});

const onSubmit = (formData: Partial<Coupon>) => {
  const couponType = data.value?.type;
  updateMutation.mutate({
    id: route.params.id as string,
    update:
      couponType === CouponType.PROMO
        ? {
            isActive: formData.isActive,
            validFrom: formData.validFrom != null ? new Date(formData.validFrom) : null,
            validTo: formData.validTo != null ? new Date(formData.validTo) : null,
            benefits: formData.benefits ?? undefined,
            oneRedemptionPerCustomer: formData.oneRedemptionPerCustomer,
            maxRedemptions: formData.maxRedemptions ?? null,
          }
        : {
            remainingValue: formData.remainingValue ?? undefined,
            isActive: formData.isActive,
            customerData: formData.customerData,
            validTo: formData.validTo != null ? new Date(formData.validTo) : null,
            comment: couponType === CouponType.GOODWILL ? formData.comment : undefined,
          },
  });
};

const handleDelete = async () => {
  const { isConfirmed } = await Alert.fire({
    text: t('reallyDeleteCouponText'),
    icon: 'warning',
    buttons: [
      {
        text: t('delete'),
        style: 'danger',
        propertyName: 'isConfirmed',
      },
      {
        text: t('cancel'),
        style: 'ghost',
        propertyName: 'isDismissed',
      },
    ],
  });
  if (!isConfirmed) return;
  deleteMutation.mutate(route.params.id as string);
};
</script>

<i18n lang="json">
{
  "en": {
    "back": "Back",
    "editCoupon": "Edit Coupon",
    "save": "Save",
    "couponCode": "Coupon Code",
    "bookings": "Bookings with this coupon",
    "showCouponLink": "Show coupon link",
    "reallyDeleteCouponText": "Do you really want to delete this coupon?",
    "couponUpdated": "Coupon updated successfully",
    "couponDeleted": "Coupon deleted successfully"
  },
  "de": {
    "back": "Zurück",
    "editCoupon": "Gutschein bearbeiten",
    "save": "Speichern",
    "couponCode": "Gutschein-Code",
    "bookings": "Buchungen mit diesem Gutschein",
    "showCouponLink": "Gutscheinlink anzeigen",
    "reallyDeleteCouponText": "Möchtest du diesen Gutschein wirklich löschen?",
    "couponUpdated": "Gutschein erfolgreich aktualisiert",
    "couponDeleted": "Gutschein erfolgreich gelöscht"
  },
  "es": {
    "back": "Volver",
    "editCoupon": "Editar cupón",
    "save": "Guardar",
    "couponCode": "Código del cupón",
    "bookings": "Reservas con este cupón",
    "showCouponLink": "Mostrar enlace del cupón",
    "reallyDeleteCouponText": "¿Seguro que desea eliminar este cupón?",
    "couponUpdated": "Cupón actualizado correctamente",
    "couponDeleted": "Cupón eliminado correctamente"
  }
}
</i18n>
